import type { ReactNode } from 'react';

import { CloseButton } from '@/components/common/close-button';

import styles from './overlay-chrome.module.scss';
import { OverlayPattern } from './overlay-pattern';

export type OverlayChromeProps = {
  /** When set, renders the cream header band with title + pattern. */
  title?: ReactNode;
  /** Optional supporting line under the title, inside the header band. A sibling of the <h2>, not a
   *  child: nesting it would fold it into the heading's accessible name. */
  subtitle?: ReactNode;
  onClose: () => void;
  closeAriaLabel?: string;
  /** Footer node (typically <OverlayFooter />). Omitted when absent. */
  footer?: ReactNode;
  /** When provided, body + footer are wrapped in a <form> with this handler. */
  onSubmit?: React.ComponentProps<'form'>['onSubmit'];
  /**
   * Extra class on the body region. The default body is a padded scroller; a panel that needs to
   * pin a header and scroll only part of itself (the History / Alerts drawers) overrides that
   * here rather than fighting the padding with negative margins.
   */
  bodyClassName?: string;
  children: ReactNode;
};

export function OverlayChrome({
  title,
  subtitle,
  onClose,
  closeAriaLabel = 'Close',
  footer,
  onSubmit,
  bodyClassName,
  children,
}: OverlayChromeProps) {
  const inner = (
    <>
      {title != null ? (
        <header className={styles.header}>
          <div className={styles.headings}>
            <h2 className={styles.title}>{title}</h2>
            {subtitle != null ? <p className={styles.subtitle}>{subtitle}</p> : null}
          </div>
          <OverlayPattern />
        </header>
      ) : null}
      <div className={[styles.body, bodyClassName].filter(Boolean).join(' ')}>{children}</div>
      {footer ? <div className={styles.footerRegion}>{footer}</div> : null}
    </>
  );

  return (
    <div className={styles.content}>
      <CloseButton aria-label={closeAriaLabel} onClick={onClose} className={styles.close} />
      {onSubmit ? (
        <form className={styles.form} onSubmit={onSubmit} noValidate>
          {inner}
        </form>
      ) : (
        inner
      )}
    </div>
  );
}
